import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import { formatCurrency } from '../../utils/formatMoeda';
import { formatDateView } from '../../utils/dateUtils';
import usePagination10 from '../hooks/usePagination10';
import useDefaultModal from '../hooks/useDefaultModal';
import useConfirmModal from '../hooks/useConfirmModal';
import DefaultModal from './DefaultModal';
import ConfirmModal from './ConfirmModal';
import TableContainer from '../TableContainer';
import DataTable from '../DataTable';
import PaginationBar from '../PaginationBar';

function PesquisaPrecoMaterialListaModal({ show, onClose, demanda, item, onDelete, buscarPrecos }) {
  const [resultados, setResultados] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'precoUnitario', direction: 'desc' });
  const token = localStorage.getItem('token');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [filtroPrecoMin, setFiltroPrecoMin] = useState(null);
  const [filtroPrecoMax, setFiltroPrecoMax] = useState(null);
  const [filtroPeriodo, setFiltroPeriodo] = useState('');
  const [filtroUnidade, setFiltroUnidade] = useState('');

  const handleDelete = (r) => {
    setRecordToDelete(r);
    showConfirmModal('delete', () => confirmDelete(r));
  };

  const confirmDelete = (r) => {
    axios
      .delete(`${API_BASE_URL.PESQUISA_PRECO_MATERIAL}/delete`, {
        params: {
          cnpj: r.cnpj,
          demandaNum: r.demandaNum,
          itemId: r.id,
          idCompra: r.idCompra,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        setResultados(prev => prev.filter(item => item.idCompra !== r.idCompra));
        showDefaultModal('success', 'Registro excluído com sucesso!');
        if (onDelete) onDelete();
      })
      .catch(error => {
        console.error('Erro ao excluir registro:', error);
        showDefaultModal('error', 'Erro ao excluir registro.');
      });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <i className="bi bi-arrow-down-up ms-1" style={{ fontSize: '0.6rem', opacity: 0.4 }}></i>;
    return sortConfig.direction === 'asc'
      ? <i className="bi bi-sort-numeric-down ms-1" style={{ fontSize: '0.6rem' }}></i>
      : <i className="bi bi-sort-numeric-up ms-1" style={{ fontSize: '0.6rem' }}></i>;
  };

  const resultadosPeriodo = React.useMemo(() => {
    if (!filtroPeriodo) return resultados;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - parseInt(filtroPeriodo));
    return resultados.filter(r => {
      if (!r.dataCompra) return false;
      return new Date(r.dataCompra) >= cutoff;
    });
  }, [resultados, filtroPeriodo]);

  const unidades = React.useMemo(() => {
    const set = new Set(resultadosPeriodo.map(r => r.siglaUnidadeFornecimento).filter(Boolean));
    return Array.from(set).sort();
  }, [resultadosPeriodo]);

  const priceRange = React.useMemo(() => {
    if (resultadosPeriodo.length === 0) return { min: 0, max: 100 };
    const prices = resultadosPeriodo.map(r => r.precoUnitario).filter(p => p != null);
    if (prices.length === 0) return { min: 0, max: 100 };
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }, [resultadosPeriodo]);

  const filteredResultados = React.useMemo(() => {
    let filtered = [...resultadosPeriodo];
    if (filtroUnidade) filtered = filtered.filter(r => r.siglaUnidadeFornecimento === filtroUnidade);
    if (filtroPrecoMin != null) filtered = filtered.filter(r => r.precoUnitario >= filtroPrecoMin);
    if (filtroPrecoMax != null) filtered = filtered.filter(r => r.precoUnitario <= filtroPrecoMax);
    return filtered;
  }, [resultadosPeriodo, filtroUnidade, filtroPrecoMin, filtroPrecoMax]);

  const itensForaFiltro = React.useMemo(() => {
    if (resultados.length === 0 || filteredResultados.length === 0) return [];
    const filteredIds = new Set(filteredResultados.map(r => r.idCompra));
    return resultados.filter(r => !filteredIds.has(r.idCompra));
  }, [resultados, filteredResultados]);

  const handleEliminarExcluidos = () => {
    const itens = itensForaFiltro;
    showConfirmModal('delete', () => {
      setLoading(true);
      const promises = itens.map(r =>
        axios.delete(`${API_BASE_URL.PESQUISA_PRECO_MATERIAL}/delete`, {
          params: { cnpj: r.cnpj, demandaNum: r.demandaNum, itemId: r.id, idCompra: r.idCompra },
          headers: { Authorization: `Bearer ${token}` },
        })
      );
      Promise.all(promises)
        .then(() => {
          const excluidos = new Set(itens.map(r => r.idCompra));
          setResultados(prev => prev.filter(r => !excluidos.has(r.idCompra)));
          showDefaultModal('success', `${itens.length} registro(s) excluído(s) com sucesso!`);
          if (onDelete) onDelete();
        })
        .catch(error => {
          console.error('Erro ao excluir registros:', error);
          showDefaultModal('error', 'Erro ao excluir registros.');
        })
        .finally(() => setLoading(false));
    });
  };

  const mediaSaneada = React.useMemo(() => {
    const prices = filteredResultados.map(r => r.precoUnitario).filter(p => p != null);
    if (prices.length === 0) return null;
    const sorted = [...prices].sort((a, b) => a - b);
    const trim = Math.max(1, Math.floor(sorted.length * 0.1));
    const trimmed = sorted.slice(trim, sorted.length - trim);
    if (trimmed.length === 0) return sorted.reduce((a, b) => a + b, 0) / sorted.length;
    return trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
  }, [filteredResultados]);

  const sliderTrackRef = React.useRef(null);
  const [dragging, setDragging] = useState(null);
  const filtroRef = React.useRef({ min: null, max: null });
  filtroRef.current = { min: filtroPrecoMin, max: filtroPrecoMax };

  const pctFromValue = (val) => {
    const range = priceRange.max - priceRange.min;
    if (range === 0) return 0;
    return ((val - priceRange.min) / range) * 100;
  };

  const valueFromPct = (pct) => {
    const range = priceRange.max - priceRange.min;
    const raw = priceRange.min + (pct / 100) * range;
    return Math.round(raw * 100) / 100;
  };

  const clamp = (val, lo, hi) => Math.max(lo, Math.min(hi, val));

  React.useEffect(() => {
    if (!dragging) return;

    const onMove = (e) => {
      if (!sliderTrackRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const rect = sliderTrackRef.current.getBoundingClientRect();
      const pct = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      const range = priceRange.max - priceRange.min;
      const raw = priceRange.min + (pct / 100) * range;
      const val = Math.round(raw * 100) / 100;
      const curMin = filtroRef.current.min ?? priceRange.min;
      const curMax = filtroRef.current.max ?? priceRange.max;
      if (dragging === 'min') {
        setFiltroPrecoMin(clamp(val, priceRange.min, curMax));
      } else {
        setFiltroPrecoMax(clamp(val, curMin, priceRange.max));
      }
    };

    const onUp = () => setDragging(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [dragging, priceRange]);

  const handleTrackClick = (e) => {
    if (!sliderTrackRef.current) return;
    const rect = sliderTrackRef.current.getBoundingClientRect();
    const pct = clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);
    const val = valueFromPct(pct);
    const curMin = filtroPrecoMin ?? priceRange.min;
    const curMax = filtroPrecoMax ?? priceRange.max;
    if (Math.abs(val - curMin) <= Math.abs(val - curMax)) {
      setFiltroPrecoMin(clamp(val, priceRange.min, curMax));
    } else {
      setFiltroPrecoMax(clamp(val, curMin, priceRange.max));
    }
  };

  const sortedResultados = React.useMemo(() => {
    const sorted = [...filteredResultados];
    if (!sortConfig.key) return sorted;
    sorted.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (valA == null) valA = '';
      if (valB == null) valB = '';
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      }
      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredResultados, sortConfig]);

  const totalItems = sortedResultados.length;
  const { setPagination } = usePagination10(totalItems);
  const { startIndex, rowsPerPage } = setPagination;
  const selectedResultados = sortedResultados.slice(startIndex, startIndex + rowsPerPage);

  const fetchResultados = () => {
    if (!demanda || !item) return;
    setLoading(true);
    axios
      .get(`${API_BASE_URL.PESQUISA_PRECO_MATERIAL}/resultados/item`, {
        params: {
          cnpj: demanda.cnpj,
          demandaNum: demanda.num,
          itemId: item.id,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setResultados(result.data || []);
      })
      .catch(error => {
        console.error('Erro ao buscar preços do item:', error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!show || !demanda || !item) return;
    fetchResultados();
  }, [show, demanda, item, token]);

  useEffect(() => {
    if (show) {
      setPagination.resetPage();
    } else {
      setResultados([]);
      setSortConfig({ key: 'precoUnitario', direction: 'desc' });
      setFiltroPrecoMin(null);
      setFiltroPrecoMax(null);
      setFiltroPeriodo('');
      setFiltroUnidade('');
    }
  }, [show]);

  useEffect(() => {
    setPagination.resetPage();
  }, [filtroPeriodo, filtroUnidade, filtroPrecoMin, filtroPrecoMax]);

  if (!show) return null;

  return (
    <>
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: '96vw', height: '90vh' }}
      >
        <div
          className="modal-content"
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '0.5rem',
            overflow: 'hidden',
          }}
        >
          <div
            className="modal-header text-white"
            style={{
              background:
                'linear-gradient(45deg, rgb(19, 72, 156) 0%, rgba(19, 72, 156, 1) 100%)',
              padding: '0.35rem 0.75rem',
              border: 'none',
              flexShrink: 0,
            }}
          >
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ width: '100%' }}
            >
              <h6
                className="modal-title mb-0"
                style={{ fontSize: '0.80rem', lineHeight: 1 }}
              >
                <i className="bi bi-search-dollar me-2"></i>
                Preços ComprasGov
              </h6>
              <div className="d-flex align-items-center gap-2">
                {buscarPrecos && (
                  <button className="btn btn-sm btn-outline-light d-inline-flex align-items-center py-0" style={{ fontSize: '0.72rem' }}
                    onClick={() => showConfirmModal('atualizarPrecos', () => buscarPrecos(showDefaultModal, fetchResultados))} title="Buscar preços na ComprasGov">
                    <i className="bi bi-search-dollar me-1"></i>Buscar Preços
                  </button>
                )}
                <button
                  className="btn btn-link text-white p-0 mb-0"
                  onClick={onClose}
                  title="Fechar"
                  style={{ fontSize: '1rem', textDecoration: 'none' }}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          </div>
          <div
            className="modal-body"
            style={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              padding: '0.75rem',
            }}
          >
            {isLoading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </div>
                <p className="mt-2">Carregando preços...</p>
              </div>
            )}
            {!isLoading && (
              <>
              <div className="card mb-2" style={{ fontSize: '0.75rem' }}>
                <div className="card-body py-1 px-2 d-flex gap-3">
                  <span><strong>Código Catmat:</strong> {item?.item}</span>
                  <span><strong>Descrição:</strong> {item?.descricao}</span>
                </div>
              </div>
              <div className="mb-2 p-2 rounded" style={{ fontSize: '0.72rem', background: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                <div className="d-flex align-items-center gap-3">
                  <div className="d-flex align-items-center gap-1">
                    <i className="bi bi-funnel"></i>
                    <span className="fw-semibold">Filtros:</span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <label className="mb-0">Período:</label>
                    <select className="form-select form-select-sm py-0" style={{ width: '130px', fontSize: '0.72rem' }} value={filtroPeriodo} onChange={e => setFiltroPeriodo(e.target.value)}>
                      <option value="">Todos</option>
                      <option value="30">Últimos 30 dias</option>
                      <option value="60">Últimos 60 dias</option>
                      <option value="90">Últimos 90 dias</option>
                      <option value="120">Últimos 120 dias</option>
                      <option value="180">Últimos 180 dias</option>
                      <option value="365">Últimos 365 dias</option>
                    </select>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <label className="mb-0">Unidade:</label>
                    <select className="form-select form-select-sm py-0" style={{ width: '100px', fontSize: '0.72rem' }} value={filtroUnidade} onChange={e => setFiltroUnidade(e.target.value)}>
                      <option value="">Todas</option>
                      {unidades.map(u => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  </div>
                    <div style={{ width: '340px' }}>
                      <div className="d-flex align-items-center gap-2">
                        <label className="mb-0 fw-semibold text-nowrap">Faixa de preço:</label>
                        <span className="text-muted" style={{ fontSize: '0.72rem', minWidth: '55px', textAlign: 'right' }}>{formatCurrency(filtroPrecoMin ?? priceRange.min)}</span>
                        <div ref={sliderTrackRef} className="position-relative flex-grow-1" style={{ height: '20px', cursor: 'pointer' }} onClick={handleTrackClick}>
                          <div className="position-absolute rounded" style={{ height: '4px', top: '8px', background: '#dee2e6', left: 0, right: 0 }}></div>
                          <div className="position-absolute rounded" style={{ height: '4px', top: '8px', background: '#0d6efd',
                            left: `${pctFromValue(filtroPrecoMin ?? priceRange.min)}%`,
                            right: `${100 - pctFromValue(filtroPrecoMax ?? priceRange.max)}%`
                          }}></div>
                          <div className="position-absolute" onMouseDown={(e) => { e.stopPropagation(); setDragging('min'); }} onTouchStart={(e) => { e.stopPropagation(); setDragging('min'); }}
                            style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#0d6efd', border: '2px solid #fff', boxShadow: '0 0 3px rgba(0,0,0,0.3)',
                              top: '3px', cursor: 'grab', zIndex: dragging === 'min' ? 5 : 2,
                              left: `calc(${pctFromValue(filtroPrecoMin ?? priceRange.min)}% - 7px)`
                            }}></div>
                          <div className="position-absolute" onMouseDown={(e) => { e.stopPropagation(); setDragging('max'); }} onTouchStart={(e) => { e.stopPropagation(); setDragging('max'); }}
                            style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#0d6efd', border: '2px solid #fff', boxShadow: '0 0 3px rgba(0,0,0,0.3)',
                              top: '3px', cursor: 'grab', zIndex: dragging === 'max' ? 5 : 3,
                              left: `calc(${pctFromValue(filtroPrecoMax ?? priceRange.max)}% - 7px)`
                            }}></div>
                        </div>
                        <span className="text-muted" style={{ fontSize: '0.72rem', minWidth: '55px' }}>{formatCurrency(filtroPrecoMax ?? priceRange.max)}</span>
                      </div>
                    </div>
                  {mediaSaneada != null && (
                    <div className="d-flex align-items-center gap-1 ms-2">
                      <i className="bi bi-calculator"></i>
                      <span className="fw-semibold">Média saneada:</span>
                      <span className="fw-bold" style={{ color: '#0d6efd' }}>{formatCurrency(mediaSaneada)}</span>
                    </div>
                  )}
                  {(filtroPeriodo || filtroUnidade || filtroPrecoMin != null || filtroPrecoMax != null) && (
                    <>
                      <button className="btn btn-sm py-0" style={{ fontSize: '0.72rem', color: '#dc3545' }} onClick={handleEliminarExcluidos} title={`Eliminar ${itensForaFiltro.length} registro(s) fora do filtro`}>
                        <i className="bi bi-scissors me-1"></i>Eliminar fora do filtro ({itensForaFiltro.length})
                      </button>
                      <button className="btn btn-sm py-0" style={{ fontSize: '0.72rem', color: '#dc3545' }} onClick={() => { setFiltroPeriodo(''); setFiltroUnidade(''); setFiltroPrecoMin(null); setFiltroPrecoMax(null); }}>
                        <i className="bi bi-x-lg me-1"></i>Limpar
                      </button>
                    </>
                  )}
                </div>
              </div>
              <TableContainer isLoading={false}>
                <DataTable
                  style={{ fontSize: '0.75rem' }}
                  head={
                    <tr>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('nomeFornecedor')}>Fornecedor{sortIcon('nomeFornecedor')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('marca')}>Marca{sortIcon('marca')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('siglaUnidadeFornecimento')}>Unidade{sortIcon('siglaUnidadeFornecimento')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('quantidade')}>Quantidade{sortIcon('quantidade')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('precoUnitario')}>Preço Unit.{sortIcon('precoUnitario')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('dataCompra')}>Data Compra{sortIcon('dataCompra')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('nomeUasg')}>UASG{sortIcon('nomeUasg')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('municipio')}>Município{sortIcon('municipio')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('estado')}>UF{sortIcon('estado')}</th>
                      <th className="text-center align-middle" style={{ cursor: 'pointer' }} onClick={() => handleSort('modalidade')}>Modalidade{sortIcon('modalidade')}</th>
                      <th className="text-center align-middle" style={{ width: '3%' }}></th>
                    </tr>
                  }
                >
                  <tbody>
                    {selectedResultados.length === 0 ? (
                      <tr>
                        <td colSpan="11" className="text-center py-4">
                          <i className="bi bi-info-circle me-2 text-muted"></i>Nenhum preço encontrado para este item.
                        </td>
                      </tr>
                    ) : selectedResultados.map((r, i) => (
                      <tr key={`${r.idCompra}-${r.idItemCompra}-${i}`}>
                        <td>{r.nomeFornecedor}</td>
                        <td>{r.marca}</td>
                        <td className="text-center">{r.siglaUnidadeFornecimento}</td>
                        <td className="text-end">{r.quantidade}</td>
                        <td className="text-end">
                          {r.precoUnitario ? formatCurrency(r.precoUnitario) : '-'}
                        </td>
                        <td className="text-center">
                          {r.dataCompra ? formatDateView(r.dataCompra) : '-'}
                        </td>
                        <td>{r.nomeUasg}</td>
                        <td>{r.municipio}</td>
                        <td className="text-center">{r.estado}</td>
                        <td className="text-center">{r.modalidade}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-outline-danger btn-sm p-0"
                            style={{ fontSize: '0.7rem', lineHeight: 1, padding: '2px 5px' }}
                            title="Excluir registro"
                            onClick={() => handleDelete(r)}
                          >
                            <i className="bi bi-trash3"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </TableContainer>
              </>
            )}
          </div>
          {resultados.length > 0 && <PaginationBar setPagination={setPagination} />}
        </div>
      </div>
    </div>
      <div style={{ position: 'fixed', zIndex: 1070 }}>
        <ConfirmModal {...setConfirmModal} />
        <DefaultModal {...setDefaultModal} />
      </div>
    </>
  );
}

export default PesquisaPrecoMaterialListaModal;
